<template>
  <div class="ui basic segment" style="padding-right: 1.5em;">
    <run-flow :ctrl="runFlowModalCtrl" :manatee="id" />

    <modal
      :ctrl="confirmModalCtrl"
      :modalClasses="['mini']"
      :positiveLabel="{ text: $t('YES'), color: 'red' }"
      :negativeLabel="{ text: $t('NO'), color: 'green' }"
    />
    <h3 class="header">
      {{ $t('ESTERO_DETAILS', { id }) }}
      &nbsp;
      <div class="ui empty circular label" :class="{ black: !connected, green: connected }"></div>
    </h3>
    <div class="active content detail-content">
      <div class="ui two column grid">
        <div class="eight wide column">
          <div class="ui middle aligned relaxed list">
            <div class="item">
              <div class="right floated monospaced content">{{ env['Env/User'] }}</div>
              <div class="content">{{ $t('USERNAME') }}</div>
            </div>
            <div class="item">
              <div class="right floated monospaced content">{{ env['Env/MachineName'] }}</div>
              <div class="content">{{ $t('MACHINENAME') }}</div>
            </div>
            <div class="item">
              <div class="right floated monospaced content">{{ env['Env/Domain'] }}</div>
              <div class="content">{{ $t('USERDOMAIN') }}</div>
            </div>
            <div class="item">
              <div class="right floated monospaced content">{{ env['Env/Version'] }}</div>
              <div class="content">{{ $t('MANATEE_VERSION') }}</div>
            </div>
            <div class="item">
              <div class="right floated monospaced content">{{ env['Env/BranchName'] }}</div>
              <div class="content">{{ $t('MANATEE_BRANCH') }}</div>
            </div>
            <div class="item" v-if="connected">
              <div class="right floated monospaced content">{{ uptime }}</div>
              <div class="content">{{ $t('MANATEE_UPTIME') }}</div>
            </div>

            <div class="item" v-if="connected">
              <div class="right floated content">
                <running-flow :flow="liveManatee['Manatee/Status/Flow']" />
              </div>
              <div class="content">{{ $t('RUNNING_FLOW') }}</div>
            </div>
          </div>
        </div>
        <div class="six wide right aligned right floated column">
          <div id="actionMenu" class="ui fluid secondary vertical menu">
            <a class="item" @click="showRunFlowDialog">
              <i class="shuffle icon" /> {{ $t('ESTERO_RUN_FLOW') }} ...
            </a>
            <a
              class="item"
              v-confirm-click="{
                modalController: confirmModalCtrl,
                onConfirm: reset,
                confirmHeader: $t('ESTERO_RESET_SETTINGS'),
                confirmMessage: $t('ESTERO_CONFIRM', { action: $t('ESTERO_RESET_SETTINGS') })
              }"
            >
              <i class="tasks icon" /> {{ $t('ESTERO_RESET_SETTINGS') }}
            </a>
            <a
              class="item"
              v-confirm-click="{
                modalController: confirmModalCtrl,
                onConfirm: restart,
                confirmHeader: $t('ESTERO_RESTART'),
                confirmMessage: $t('ESTERO_CONFIRM', { action: $t('ESTERO_RESTART') })
              }"
            >
              <i class="yellow undo alternate icon" /> {{ $t('ESTERO_RESTART') }}
            </a>
            <a
              class="item"
              v-confirm-click="{
                modalController: confirmModalCtrl,
                onConfirm: shutdown,
                confirmHeader: $t('ESTERO_SHUTDOWN'),
                confirmMessage: $t('ESTERO_CONFIRM', { action: $t('ESTERO_SHUTDOWN') })
              }"
            >
              <i class="red power off icon" /> {{ $t('ESTERO_SHUTDOWN') }}
            </a>
          </div>
        </div>
      </div>

      <div class="ui top attached tabular menu">
        <div
          class="item"
          :class="{ active: currentTab === 'settings' }"
          @click="currentTab = 'settings'"
        >
          {{ $t('SETTINGS') }}
        </div>
        <div
          class="item"
          :class="{ active: currentTab === 'groups' }"
          @click="currentTab = 'groups'"
        >
          {{ $tc('GROUP', 2) | capitalize }}
          <div class="ui mini circular label">{{ groupsList.length }}</div>
        </div>
        <div
          class="item"
          :class="{ active: currentTab === 'alerts' }"
          @click="currentTab = 'alerts'"
        >
          {{ $tc('ALERT', 2) | capitalize }}
          <div class="ui mini circular label">{{ alertRules.length }}</div>
        </div>
      </div>

      <!-- SETTINGS -->
      <div class="ui bottom attached tab segment" :class="{ active: currentTab === 'settings' }">
        <div class="ui grid">
          <div
            :class="{
              'sixteen wide column': changedSettings.length == 0,
              'eight wide column': changedSettings.length > 0
            }"
          >
            <div class="ui small fluid icon input">
              <input type="text" :placeholder="$t('SEARCH')" v-model="settingsSearchTerm" />
              <i class="search icon"></i>
            </div>
          </div>
          <div
            class="six wide right floated right aligned column"
            v-if="changedSettings.length > 0"
          >
            <div class="ui green small labeled icon button" @click="updateSettings">
              {{ $tc('ESTERO_SAVE_SETTINGS', changedSettings.length) }}
              <i class="ui cloud upload alternate icon" />
            </div>
            <div class="ui yellow small labeled icon button" @click="undoSettings">
              {{ $tc('ESTERO_RESET_SETTINGS_CHANGES', changedSettings.length) }}
              <i class="ui undo alternate icon" />
            </div>
          </div>
        </div>
        <div class="ui middle aligned relaxed selection scroller list">
          <div
            class="item"
            v-for="s in filteredSettingsList"
            :key="s.key"
            :class="{ changed: s.value != s.originalValue }"
          >
            <div class="right floated content">
              <i class="ui pen icon" v-if="s.value != s.originalValue" />
              <div class="ui input" v-if="s.datatype == 'number'">
                <input type="number" v-model.number="s.value" />
              </div>
              <div class="ui input" v-if="s.datatype == 'string'">
                <input type="text" v-model="s.value" />
              </div>
              <div class="ui toggle checkbox" v-if="s.datatype == 'boolean'">
                <input id="s.key" type="checkbox" v-model="s.value" />
                <label for="s.key"> </label>
              </div>
            </div>
            <div class="content" :class="{ changed: s.value != s.originalValue }">
              <div class="header">{{ s.key }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- GROUPS -->
      <div class="ui bottom attached tab segment" :class="{ active: currentTab === 'groups' }">
        <div class="ui small fluid icon input">
          <input type="text" :placeholder="$t('SEARCH')" v-model="groupsSearchTerm" />
          <i class="search icon" />
        </div>
        <div class="ui middle aligned relaxed selection scroller list">
          <div class="item" v-for="g in filteredGroupsList" :key="g">
            <div class="content">{{ g }}</div>
          </div>
        </div>
      </div>

      <!-- ALERTS -->
      <div class="ui bottom attached tab segment" :class="{ active: currentTab === 'alerts' }">
        <div class="ui middle aligned relaxed selection scroller list">
          <AlertRuleList
            :rules="alertRules"
            @add="addAlertRule"
            @change="onAlertChange"
            @delete="onAlertDelete"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';

.button:first-letter {
  text-transform: uppercase;
}

.scroller {
  overflow-y: scroll;
}

.changed.item {
}

.changed.content {
  font-weight: bold;
}

#actionMenu .item {
  text-transform: capitalize;
}

.detail-content {
  flex: 1 0 100px;

  display: flex;
  flex-direction: column;

  .bottom.attached.tab.segment {
    &.active {
      flex: 1 0 100px;
      display: flex;
      flex-direction: column;
      .scroller.list {
        flex: 1 0 100px;
      }
    }
  }
}
</style>

<script>
// @flow
import { Estero, EsteroSocket, Toast, EsteroAlert } from 'services'
import { Modal, ModalController } from 'ui'
import RunningFlow from './RunningFlow'
import RunFlow from './RunFlow'
import AlertRuleList from './AlertRuleList'
import moment from 'moment'
import fuzzy from 'fuzzy'

const datatypeFromValue = v => {
  if (/^\d+$/.test(v)) return 'number'
  if (v.toLowerCase() === 'true' || v.toLowerCase() === 'false') return 'boolean'
  return 'string'
}

export default {
  name: 'estero-details',

  components: { RunningFlow, RunFlow, Modal, AlertRuleList },

  props: {
    id: { type: String, default: () => null },
    defaultUptime: { type: String, default: () => '...' },
    operators: { type: Object, default: () => {} }
  },

  data() {
    return {
      env: {},
      settings: [],
      liveManatee: {},
      currentTab: 'settings',
      uptimeInterval: null,
      uptimeStart: null,
      uptime: this.defaultUptime,
      settingsSearchTerm: null,
      groupsSearchTerm: null,
      runFlowModalCtrl: new ModalController({ header: this.$i18n.t('ESTERO_RUN_FLOW') }),
      confirmModalCtrl: new ModalController()
    }
  },

  computed: {
    alertRules() {
      return _.filter(
        this.$store.state.estero_alert.items,
        r =>
          r.target.user === this.env['Env/User'] && r.target.machine === this.env['Env/MachineName']
      )
    },

    connected() {
      return (
        this.liveManatee !== null &&
        this.liveManatee !== undefined &&
        this.liveManatee['Manatee/Identifier'] !== null &&
        this.liveManatee['Manatee/Identifier'] !== undefined
      )
    },

    changedSettings() {
      return _.filter(this.settings, s => s.value != s.originalValue)
    },

    initialUptime() {
      return parseInt(this.env['Env/Uptime'], 10)
    },

    filteredGroupsList() {
      return this.groupsSearchTerm
        ? fuzzy.filter(this.groupsSearchTerm, this.groupsList).map(hit => hit.original)
        : this.groupsList
    },

    filteredSettingsList() {
      return this.settingsSearchTerm
        ? fuzzy
            .filter(this.settingsSearchTerm, this.settings, {
              extract: s => `${s.key} ${s.value}`
            })
            .map(hit => hit.original)
        : this.settings
    },

    groupsList() {
      const groups = this.env['Env/Groups']
      if (groups) return groups.split(',')
      return []
    }
  },

  methods: {
    onAlertChange(rule) {
      // Set user and machine as target on alert rule
      rule.target = { user: this.env['Env/User'], machine: this.env['Env/MachineName'] }
      this.toastIt(EsteroAlert.update(rule.id, rule), 'ESTERO_SAVE_ALERT')
    },
    onAlertDelete(rule) {
      this.toastIt(EsteroAlert.remove(rule.id), 'ESTERO_REMOVE_ALERT')
    },
    addAlertRule() {
      // Create an empty new rule
      this.toastIt(
        EsteroAlert.create({
          enabled: true,
          target: { user: this.env['Env/User'], machine: this.env['Env/MachineName'] }
        }),
        'ESTERO_CREATE_ALERT'
      )
    },
    updateSettings() {
      this.toastIt(
        Estero.updateSettings(
          this.id,
          _.reduce(
            this.changedSettings,
            (memo, s) => {
              memo[s.key] = `${s.value}`
              return memo
            },
            {}
          )
        ),
        'ESTERO_SAVE_SETTINGS'
      ).then(() => _.each(this.settings, s => (s.originalValue = s.value)))
    },
    undoSettings() {
      _.each(this.settings, s => (s.value = s.originalValue))
    },
    updateUptime() {
      if (!this.connected) this.uptime = this.defaultUptime
      if (this.initialUptime) {
        this.uptime = moment.duration(Date.now() - this.uptimeStart + this.initialUptime).humanize()
      }
    },
    toastIt(promise, i18n) {
      return promise
        .then(() => Toast.success(this.$i18n.t('ESTERO_SUCCESS', { action: this.$i18n.t(i18n) })))
        .catch(err => Toast.error(this.$i18n.t('ESTERO_FAIL', { action: this.$i18n.t(i18n) })))
    },
    showRunFlowDialog() {
      this.runFlowModalCtrl
        .show()
        .then(console.debug)
        .catch(console.warn)
    },
    reset() {
      this.toastIt(Estero.reset(this.id), 'ESTERO_RESET')
    },
    restart() {
      this.toastIt(Estero.restart(this.id), 'ESTERO_RESTART')
    },
    shutdown() {
      this.toastIt(Estero.shutdown(this.id), 'ESTERO_SHUTDOWN')
    },

    loadDetails() {
      Estero.inspect(this.id).then(details => {
        this.uptimeStart = Date.now()

        this.env = _.pickBy(details, (_, name) => name.indexOf('Env') == 0)

        this.settings = _.orderBy(
          _.filter(
            _.map(details, (value, k) => {
              const [type, key] = k.split('/')
              const dt = datatypeFromValue(value)
              if (dt === 'boolean') value = value.toLowerCase() === 'true'
              if (dt === 'number') value = parseInt(value, 10)
              return { type, key, value, datatype: dt, originalValue: value }
            }),
            s => s && s.type === 'Settings'
          ),
          'key'
        )
      })
    }
  },

  mounted() {
    // Load details for the Manatee instance
    this.loadDetails()

    // Establish socket for real-time info about this Manatee
    this.socket = EsteroSocket()
    this.socket.on('close', () =>
      Toast.error(
        this.$i18n.t('ESTERO_CONNECTION_LOST_HEADER'),
        this.$i18n.t('ESTERO_CONNECTION_LOST_BODY')
      )
    )
    this.socket.on('message', p => {
      this.liveManatee = _.head(p.items)
    })
    this.socket.on('open', () =>
      this.socket.update({
        view: `Manatee/Identifier = "${this.id}"`
      })
    )

    // Start uptime ticker
    this.uptimeInterval = setInterval(this.updateUptime, 5000)

    EsteroAlert.readAll()
  },

  destroyed() {
    if (this.uptimeInterval) clearInterval(this.uptimeInterval)
  }
}
</script>
